import { useState, useRef, useEffect } from "react";
import { Check, Loader2, Pencil, Plus, Tag, Trash2, X } from "lucide-react";
import {
  useLabels,
  useCreateLabel,
  useAttachLabel,
  useDeleteLabel,
  useDetachLabel,
  useRejectProposal,
  useUpdateLabel,
  useValidateProposal,
} from "../api/queries";
import type { Label, LabelType, PanneauLabel, Proposal } from "../api/types";

interface Props {
  panneauId: string;
  organisation: string;
  attached: PanneauLabel[];
  /** Pending `label_attach` proposals for this panneau. Rendered as
   *  ghost-style chips with Valider/Refuser; default `[]`. */
  pending?: Proposal[];
}

const COLOR_PALETTE = [
  { name: "orange", hex: "#F39200" },
  { name: "red", hex: "#E63946" },
  { name: "green", hex: "#2A9D58" },
  { name: "pink", hex: "#E91E63" },
  { name: "gray", hex: "#9b9b9b" },
];

function chipBg(color: string): string {
  const found = COLOR_PALETTE.find((c) => c.name === color);
  return found ? found.hex : "#9b9b9b";
}

const FAMILY_LABEL: Record<LabelType, string> = {
  statut: "Statut",
  tag: "Tags",
  motif: "Motifs",
};

const FAMILY_ORDER: LabelType[] = ["statut", "tag", "motif"];

function groupByFamily(rows: Label[]): Record<LabelType, Label[]> {
  const out: Record<LabelType, Label[]> = { statut: [], tag: [], motif: [] };
  for (const r of rows) out[r.label_type].push(r);
  return out;
}

/**
 * Replaces the visual ChipBar placeholder for Labels.
 *
 * UI: small row of attached labels (colored pill + name) followed by a
 * "+" button. Click the "+" → popover with the available labels for the
 * organisation; checkbox each to attach/detach. Inline "Nouveau label"
 * row at the bottom of the popover creates a new label and attaches it.
 *
 * v1 omissions: PRD §D's "agent dedup at create time" (the agent should
 * suggest existing labels with similar names); custom color picker (we
 * use a fixed 5-color palette).
 */
export function LabelsChip({
  panneauId,
  organisation,
  attached,
  pending = [],
}: Props) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const labels = useLabels(organisation);
  const createLabel = useCreateLabel(organisation);
  const attach = useAttachLabel(panneauId, organisation);
  const detach = useDetachLabel(panneauId);
  const updateLabel = useUpdateLabel(organisation, panneauId);
  const deleteLabel = useDeleteLabel(organisation, panneauId);
  const validateProp = useValidateProposal(panneauId);
  const rejectProp = useRejectProposal(panneauId);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Filter to label-attach proposals only.
  const pendingAttach = pending.filter((p) => p.type === "label_attach");

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const attachedIds = new Set(attached.map((l) => l.id));
  const grouped = labels.data ? groupByFamily(labels.data) : null;

  return (
    <div className="relative flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-1.5 max-w-[12rem]">
        {attached.map((l) => (
          <span
            key={l.id}
            className={`inline-flex items-center gap-1 pl-1.5 pr-0.5 py-0.5 rounded-full text-xs text-white ${l.label_type === "statut" ? "ring-2 ring-offset-1 ring-ink/40 dark:ring-white/40" : ""}`}
            style={{ backgroundColor: chipBg(l.color) }}
            title={`${FAMILY_LABEL[l.label_type]}: ${l.name}`}
          >
            <span>{l.name}</span>
            <button
              type="button"
              onClick={() => detach.mutate(l.id)}
              disabled={detach.isPending}
              className="rounded-full hover:bg-black/20 dark:hover:bg-white/20 px-0.5"
              aria-label={`Détacher ${l.name}`}
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </span>
        ))}
        {/* Pending label-attach proposals — ghost-style chip. Dashed
            border, lower opacity, italic name, white background so it
            doesn't get mistaken for a validated label. */}
        {pendingAttach.map((p) => {
          const name = String(p.payload.label_name ?? "");
          const color = String(p.payload.color ?? "gray");
          const isValidating =
            validateProp.isPending && validateProp.variables === p.id;
          const isRejecting =
            rejectProp.isPending && rejectProp.variables === p.id;
          return (
            <span
              key={p.id}
              className="inline-flex items-center gap-1 pl-1.5 pr-0.5 py-0.5 rounded-full text-xs italic opacity-70 border border-dashed bg-white/60 dark:bg-surface-dark/60"
              style={{ borderColor: chipBg(color), color: chipBg(color) }}
              title={`Proposition de label : ${name} — « ${p.source_quote} »`}
            >
              <span className="uppercase tracking-wide text-[10px] opacity-80">
                à valider
              </span>
              <span>{name}</span>
              <button
                type="button"
                onClick={() => validateProp.mutate(p.id)}
                disabled={isValidating || isRejecting}
                className="rounded-full hover:bg-black/10 dark:hover:bg-white/10 px-0.5"
                aria-label={`Valider le label ${name}`}
                title="Valider"
              >
                {isValidating ? (
                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                ) : (
                  <Check className="h-2.5 w-2.5" />
                )}
              </button>
              <button
                type="button"
                onClick={() => rejectProp.mutate(p.id)}
                disabled={isValidating || isRejecting}
                className="rounded-full hover:bg-black/10 dark:hover:bg-white/10 px-0.5"
                aria-label={`Refuser le label ${name}`}
                title="Refuser"
              >
                {isRejecting ? (
                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                ) : (
                  <X className="h-2.5 w-2.5" />
                )}
              </button>
            </span>
          );
        })}
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="h-5 w-5 rounded-full border border-dashed border-black/30 dark:border-white/30 text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark hover:border-ink dark:hover:border-white flex items-center justify-center"
          aria-label="Gérer les labels"
        >
          <Plus className="h-3 w-3" />
        </button>
      </div>
      <span className="text-xs text-muted dark:text-muted-dark">Labels</span>

      {open && (
        <div
          ref={popoverRef}
          className="absolute left-0 top-full mt-2 z-20 w-64 bg-white dark:bg-surface-dark rounded-xl border border-black/10 dark:border-white/10 shadow-lg p-2"
          role="dialog"
          aria-label="Gestion des labels"
        >
          {labels.isLoading && (
            <div className="text-sm text-muted dark:text-muted-dark px-2 py-1.5 flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Chargement…
            </div>
          )}
          {labels.data && labels.data.length === 0 && (
            <div className="text-xs text-muted dark:text-muted-dark px-2 py-1.5">
              Aucun label dans cette organisation. Créez-en un ci-dessous.
            </div>
          )}
          <div className="max-h-56 overflow-y-auto">
            {grouped &&
              FAMILY_ORDER.map((family) => {
                const rows = grouped[family];
                if (rows.length === 0) return null;
                return (
                  <section key={family} className="mb-1">
                    <header className="px-2 pt-1 pb-0.5 text-[10px] uppercase tracking-wide text-muted dark:text-muted-dark">
                      {FAMILY_LABEL[family]}
                      {family === "statut" && (
                        <span className="ml-1 normal-case lowercase tracking-normal text-muted/80 dark:text-muted-dark/80">
                          (un seul à la fois)
                        </span>
                      )}
                    </header>
                    <ul>
                      {rows.map((l) => {
                        const isAttached = attachedIds.has(l.id);
                        const isEditing = editingId === l.id;
                        if (isEditing) {
                          return (
                            <li key={l.id} className="px-1 py-1.5">
                              <EditLabelRow
                                label={l}
                                pending={updateLabel.isPending}
                                onSave={async (patch) => {
                                  await updateLabel.mutateAsync({ id: l.id, ...patch });
                                  setEditingId(null);
                                }}
                                onCancel={() => setEditingId(null)}
                              />
                            </li>
                          );
                        }
                        return (
                          <li key={l.id} className="flex items-center gap-1 group">
                            <button
                              type="button"
                              onClick={() =>
                                isAttached ? detach.mutate(l.id) : attach.mutate(l.id)
                              }
                              disabled={attach.isPending || detach.isPending}
                              className="flex-1 text-left px-2 py-1.5 rounded hover:bg-canvas dark:hover:bg-canvas-dark flex items-center gap-2 text-sm min-w-0"
                            >
                              <span
                                className="h-3 w-3 rounded-full shrink-0"
                                style={{ backgroundColor: chipBg(l.color) }}
                              />
                              <span className="flex-1 truncate">{l.name}</span>
                              {isAttached && (
                                <Tag
                                  className="h-3 w-3 text-ink dark:text-ink-dark"
                                  aria-label="Attaché"
                                />
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingId(l.id)}
                              className="opacity-100 p-1 text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark transition-colors"
                              aria-label={`Modifier ${l.name}`}
                              title="Modifier"
                            >
                              <Pencil className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Supprimer le label « ${l.name} » ? Il sera détaché de tous les panneaux.`,
                                  )
                                ) {
                                  deleteLabel.mutate(l.id);
                                }
                              }}
                              disabled={deleteLabel.isPending}
                              className="opacity-100 p-1 text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark transition-colors"
                              aria-label={`Supprimer ${l.name}`}
                              title="Supprimer du registre"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
          </div>
          <NewLabelForm
            organisation={organisation}
            onCreate={async (body) => {
              const created = await createLabel.mutateAsync(body);
              await attach.mutateAsync(created.id);
            }}
            pending={createLabel.isPending || attach.isPending}
          />
        </div>
      )}
    </div>
  );
}

interface NewLabelFormProps {
  organisation: string;
  onCreate: (body: {
    organisation: string;
    name: string;
    color: string;
    label_type: LabelType;
  }) => Promise<unknown>;
  pending: boolean;
}

function NewLabelForm({ organisation, onCreate, pending }: NewLabelFormProps) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("orange");
  const [family, setFamily] = useState<LabelType>("tag");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      await onCreate({ organisation, name: trimmed, color, label_type: family });
      setName("");
    } catch {
      // 409 (duplicate) — caller's mutation already shows error in console;
      // for v1 we don't surface a toast.
    }
  };

  return (
    <form onSubmit={submit} className="border-t border-black/5 dark:border-white/10 mt-1 pt-2 px-1">
      <div className="flex items-center gap-1 mb-1.5" role="radiogroup" aria-label="Famille de label">
        {FAMILY_ORDER.map((f) => (
          <button
            key={f}
            type="button"
            role="radio"
            aria-checked={family === f}
            onClick={() => setFamily(f)}
            className={`px-2 py-0.5 text-[11px] rounded-full border ${
              family === f
                ? "bg-ink text-white border-ink dark:border-white"
                : "bg-white dark:bg-surface-dark text-ink dark:text-ink-dark border-black/10 dark:border-white/10 hover:border-ink dark:hover:border-white"
            }`}
          >
            {FAMILY_LABEL[f]}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-1.5 mb-1.5">
        {COLOR_PALETTE.map((c) => (
          <button
            key={c.name}
            type="button"
            onClick={() => setColor(c.name)}
            className={`h-7 w-7 sm:h-5 sm:w-5 rounded-full ${color === c.name ? "ring-2 ring-ink dark:ring-white ring-offset-1" : ""}`}
            style={{ backgroundColor: c.hex }}
            aria-label={`Couleur ${c.name}`}
            title={c.name}
          />
        ))}
      </div>
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nouveau label…"
          maxLength={64}
          className="bun-textbox flex-1 px-2 py-1 text-sm rounded border border-black/10 dark:border-white/10 focus:outline-none focus:ring-1 focus:ring-ink dark:focus:ring-white"
        />
        <button
          type="submit"
          disabled={pending || !name.trim()}
          className="h-7 w-7 rounded-full bg-ink text-white flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Créer le label"
        >
          {pending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Plus className="h-3 w-3" />
          )}
        </button>
      </div>
    </form>
  );
}

interface EditLabelRowProps {
  label: Label;
  pending: boolean;
  onSave: (patch: { name?: string; color?: string; label_type?: LabelType }) => Promise<unknown>;
  onCancel: () => void;
}

function EditLabelRow({ label, pending, onSave, onCancel }: EditLabelRowProps) {
  const [name, setName] = useState(label.name);
  const [color, setColor] = useState(label.color);
  const [family, setFamily] = useState<LabelType>(label.label_type);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    const patch: { name?: string; color?: string; label_type?: LabelType } = {};
    if (trimmed !== label.name) patch.name = trimmed;
    if (color !== label.color) patch.color = color;
    if (family !== label.label_type) patch.label_type = family;
    if (Object.keys(patch).length === 0) {
      onCancel();
      return;
    }
    try {
      await onSave(patch);
    } catch {
      /* swallowed; v1 has no toast */
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-1.5 bg-canvas/50 dark:bg-canvas-dark/50 rounded p-1.5">
      <div className="flex items-center gap-1" role="radiogroup" aria-label="Famille">
        {FAMILY_ORDER.map((f) => (
          <button
            key={f}
            type="button"
            role="radio"
            aria-checked={family === f}
            onClick={() => setFamily(f)}
            className={`px-2 py-0.5 text-[10px] rounded-full border ${
              family === f
                ? "bg-ink text-white border-ink dark:border-white"
                : "bg-white dark:bg-surface-dark text-ink dark:text-ink-dark border-black/10 dark:border-white/10"
            }`}
          >
            {FAMILY_LABEL[f]}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-1.5">
        {COLOR_PALETTE.map((c) => (
          <button
            key={c.name}
            type="button"
            onClick={() => setColor(c.name)}
            className={`h-6 w-6 sm:h-4 sm:w-4 rounded-full ${
              color === c.name ? "ring-2 ring-ink dark:ring-white ring-offset-1" : ""
            }`}
            style={{ backgroundColor: c.hex }}
            aria-label={`Couleur ${c.name}`}
          />
        ))}
      </div>
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={64}
          autoFocus
          className="bun-textbox flex-1 px-2 py-1 text-sm rounded border border-black/10 dark:border-white/10 focus:outline-none focus:ring-1 focus:ring-ink dark:focus:ring-white"
        />
        <button
          type="submit"
          disabled={pending || !name.trim()}
          className="h-6 w-6 rounded-full bg-ink text-white flex items-center justify-center disabled:opacity-30"
          aria-label="Enregistrer"
        >
          {pending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={pending}
          className="h-6 w-6 rounded-full bg-white dark:bg-surface-dark border border-black/10 dark:border-white/10 text-muted dark:text-muted-dark flex items-center justify-center"
          aria-label="Annuler"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </form>
  );
}
